import { Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, MenuItem, TextField, Typography, useTheme } from "@mui/material";
import { Fragment, useEffect, useMemo, useState } from "react";
import CloseIcon from '@mui/icons-material/Close';

export const IPAddressDialog = ({ open, onClose, onSave, ipAddress }) => {

    const theme = useTheme();

    const [ipv4Type, setIpv4Type] = useState('wild_card');
    const [ipValue, setIpValue] = useState('');
    const [rangeStart, setRangeStart] = useState('');
    const [rangeEnd, setRangeEnd] = useState('');

    const isValidIPv4 = (value) => {
        const segments = String(value || '').trim().split('.');
        if (segments.length !== 4) return false;

        return segments.every((segment, index) => {
            if (!/^\d+$/.test(segment)) return false;
            const num = Number(segment);
            if (num < 0 || num > 255) return false;
            if (index === 0 && num === 0) return false;
            return true;
        });
    };

    const isValidWildcardIPv4 = (value) => {
        const segments = String(value || '').trim().split('.');
        if (segments.length !== 4) return false;
        if (segments[0] === '*') return false;

        return segments.every((segment) => {
            if (segment === '*') return true;
            if (!/^\d+$/.test(segment)) return false;
            const num = Number(segment);
            return num >= 0 && num <= 255;
        });
    };

    const isValidCidr = (value) => {
        const raw = String(value || '').trim();
        const [ip, prefixRaw] = raw.split('/');
        if (!ip || prefixRaw === undefined) return false;
        if (!isValidIPv4(ip)) return false;
        if (!/^\d+$/.test(prefixRaw)) return false;

        const prefix = Number(prefixRaw);
        return prefix >= 0 && prefix <= 32;
    };

    const rangeToNumber = (ip) => {
        return ip.split('.').reduce((acc, part) => (acc * 256) + Number(part), 0);
    };

    const isValidRange = (start, end) => {
        if (!isValidIPv4(start) || !isValidIPv4(end)) return false;
        return rangeToNumber(start) <= rangeToNumber(end);
    };

    const normalizedValue = useMemo(() => {
        if (ipv4Type === 'range') {
            return `${String(rangeStart || '').trim()}-${String(rangeEnd || '').trim()}`;
        }
        return String(ipValue || '').trim();
    }, [ipv4Type, ipValue, rangeStart, rangeEnd]);

    const isValidSelection = useMemo(() => {
        if (ipv4Type === 'subnet_cidr') {
            return isValidCidr(ipValue);
        }
        if (ipv4Type === 'range') {
            return isValidRange(rangeStart, rangeEnd);
        }
        return isValidWildcardIPv4(ipValue);
    }, [ipv4Type, ipValue, rangeStart, rangeEnd]);

    useEffect(() => {
        if (!open) return;

        const raw = String(ipAddress || '').trim();
        if (!raw) {
            setIpv4Type('wild_card');
            setIpValue('');
            setRangeStart('');
            setRangeEnd('');
            return;
        }

        if (raw.includes('-')) {
            const [start, end] = raw.split('-').map((part) => String(part || '').trim());
            setIpv4Type('range');
            setRangeStart(start || '');
            setRangeEnd(end || '');
            setIpValue('');
            return;
        }

        if (raw.includes('/')) {
            setIpv4Type('subnet_cidr');
            setIpValue(raw);
            setRangeStart('');
            setRangeEnd('');
            return;
        }

        setIpv4Type('wild_card');
        setIpValue(raw);
        setRangeStart('');
        setRangeEnd('');
    }, [open, ipAddress]);

    const handleConfirm = () => {
        if (!isValidSelection) {
            return;
        }
        onSave(normalizedValue);
    };

    const handleClose = () => {
        onClose();
    }

    return (
        <Fragment>
            <Dialog
                fullWidth={true}
                maxWidth={"sm"}
                open={open}
            >
                <DialogTitle
                    component="div"
                    sx={{ backgroundColor: theme.palette.primary.main, color: theme.palette.white.main, paddingY: 1, paddingX: 2 }}
                    className='flex justify-between items-center'
                >
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>IP Address Config</Typography>
                    <IconButton
                        aria-label="close"
                        onClick={handleClose}
                        sx={{
                            padding: 0,
                            color: theme.palette.white.main
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ pt: '25px !important', px: '25px', pb: '25px' }}>
                    <div className='flex flex-col gap-4'>
                        <TextField
                            select
                            label="IPv4 Type"
                            size="small"
                            value={ipv4Type}
                            onChange={(e) => setIpv4Type(e.target.value)}
                        >
                            <MenuItem value="wild_card">Static/Wild Card</MenuItem>
                            <MenuItem value="subnet_cidr">CIDR/Subnet</MenuItem>
                            <MenuItem value="range">Range</MenuItem>
                        </TextField>

                        {(ipv4Type === 'wild_card' || ipv4Type === 'subnet_cidr') && (
                            <TextField
                                fullWidth
                                size="small"
                                value={ipValue}
                                onChange={(e) => setIpValue(e.target.value)}
                                label={ipv4Type === 'wild_card' ? 'IPv4 - Static/Wild Card' : 'IPv4 - CIDR/Subnet'}
                                placeholder={ipv4Type === 'wild_card' ? '192.168.0.1 or 192.168.*.*' : '192.168.0.0/24'}
                                error={Boolean(ipValue) && !isValidSelection}
                                helperText={Boolean(ipValue) && !isValidSelection ? 'Invalid IPv4 format.' : ' '}
                            />
                        )}

                        {ipv4Type === 'range' && (
                            <div className='grid grid-cols-2 gap-3'>
                                <TextField
                                    fullWidth
                                    size="small"
                                    value={rangeStart}
                                    onChange={(e) => setRangeStart(e.target.value)}
                                    label="Range Start"
                                    placeholder="192.168.0.1"
                                    error={(Boolean(rangeStart) || Boolean(rangeEnd)) && !isValidSelection}
                                />
                                <TextField
                                    fullWidth
                                    size="small"
                                    value={rangeEnd}
                                    onChange={(e) => setRangeEnd(e.target.value)}
                                    label="Range End"
                                    placeholder="192.168.0.10"
                                    error={(Boolean(rangeStart) || Boolean(rangeEnd)) && !isValidSelection}
                                    helperText={(Boolean(rangeStart) || Boolean(rangeEnd)) && !isValidSelection ? 'Invalid IPv4 range.' : ' '}
                                />
                            </div>
                        )}
                    </div>
                </DialogContent>
                <DialogActions>
                    <div className='px-4 pb-[15px]'>
                        <Button variant="contained" disabled={!isValidSelection} onClick={() => handleConfirm()}>
                            Confirm
                        </Button>
                    </div>
                </DialogActions>
            </Dialog>
        </Fragment>
    );
};